import GeoJSON from 'geojson';

import { MultiMVTSubLayerTile, PickingInfo } from '@methanesat/maps';

import { AccordionProps as MuiAccordionProps, BarGradientProps, DrawerProps } from '@methanesat/ui-components';
import { ForwardedRef, ReactElement, ReactNode } from 'react';
import { isGeoJsonFeature, isPointFeature } from './Geojson';
import { CaptureFeatureProperties, isCaptureProperties, MethaneLayerIds } from './MapLayers';
import { PageIds, Platforms } from './MapState';
import {
    BasinFeatureProperties,
    ClusteredOGITileFeatureProperties,
    InfrastructureGeoJSONProperties,
    isClusteredPointOGITileFeature,
    isOGITileFeature,
    OGILayerIds,
    OGIPointInfrastructureIds,
    PipelineFeatureProperties,
    PointOGIMGeoJSONProperties
} from './OGI';
import {
    DppPlumeProperties,
    isPlumeProperties,
    isRasterPointProperties,
    NonDppPlumeProperites,
    PlumeProperties,
    RasterPointProperties,
    STACCollection,
    StacDateBlock
} from './STAC';

export interface PickingInfoSubset<
    G extends GeoJSON.Geometry = GeoJSON.Geometry,
    P extends GeoJSON.GeoJsonProperties = GeoJSON.GeoJsonProperties
> {
    layer: { id: string; uniqueIdProperty?: string };
    object: { id: string; collection: STACCollection } & GeoJSON.Feature<G, P>;
    tile?: MultiMVTSubLayerTile;
}

/** MSatPickInfo['object'] is a feature with geometry of type G and properties of type P */
export type MSatPickInfo<
    G extends GeoJSON.Geometry = GeoJSON.Geometry,
    P extends GeoJSON.GeoJsonProperties = GeoJSON.GeoJsonProperties
> = PickingInfoSubset<G, P> & Pick<PickingInfo, 'viewport' | 'x' | 'y' | 'pixel' | 'coordinate' | 'layer' | 'index'>;

export interface PickInfoToPrioritize<D = MSatPickInfo['object']> {
    bitmap?: BitmapInfo;
    // point sources have `parentTargetId` in the layer, and rasters have it in the object
    layer: MSatPickInfo['layer'] & { itemId?: string; parentTargetId?: string };
    object: D & { parentTargetId?: string };
    x: number;
    y: number;
    devicePixel: number[];
    index: number;
    coordinate: number[];
}

interface GenericInfo<G, P> extends Omit<MSatPickInfo, 'object'> {
    object: {
        id: string;
        properties: P;
        geometry: G;
        collection?: string;
        itemId?: string;
    };
}
interface LayerDataDrawerProps<P, G = GeoJSON.Geometry, I = GenericInfo<G, P>> {
    info: I;
    accordion?: boolean;
    AccordionProps?: MapControlAccordionProps;
}
export type PlumeDrawerProps = LayerDataDrawerProps<
    PlumeProperties<NonDppPlumeProperites | DppPlumeProperties>,
    GeoJSON.Point,
    PlumeEmissionPickInfo
>;

export type PointInfraDrawerProps = LayerDataDrawerProps<PointOGIMGeoJSONProperties>;

export type PipelineInfraDrawerProps = LayerDataDrawerProps<PipelineFeatureProperties>;

export type BasinDrawerProps = LayerDataDrawerProps<BasinFeatureProperties>;

export type AreaEmissionDrawerProps = LayerDataDrawerProps<RasterPointProperties, GeoJSON.Point, AreaEmissionPickInfo>;

/** Drawers with cards*/
export interface AccordionProps<I> {
    info: I;
    handleChange: (newExpanded: string, info: I) => void;
    expanded?: boolean;
    ogiNearbyEnabled: boolean;
}

export type DispersedSourceAccordionProps = AccordionProps<AreaEmissionPickInfo>;
export type DistinctPointSourceAccordionProps = AccordionProps<PlumeEmissionPickInfo>;
export type TotalOnlyAccordionProps = Omit<AccordionProps<TotalOnlyInfo>, 'ogiNearbyEnabled'>;

export interface DistinctPointAccordionContent {
    id: string;
    title: string;
    emissionsHeadline: ReactElement;
    dataRows: DataRow[];
    platform?: Platforms;
    showOgiDetails?: boolean;
    ogiContent: ReactElement | null;
    detailsContent: ReactElement | null;
}

export interface DispersedAccordionContent {
    id: string;
    title: string;
    emissionsHeadline: ReactElement;
    dataRows: DataRow[];
    additionalContent?: ReactElement | null;
    iconColor: string;
    platform: Platforms;
    BarGradientPropValues: BarGradientProps;
    histogramContent: ReactElement | null;
    ogiNearbyContent: ReactElement | null;
}

export interface TotalOnlyAccordionContent {
    id: string;
    title: string;
    emissionsHeadline: ReactElement;
    dataRows: DataRow[];
    iconColor: string;
    platform: Platforms;
    BarGradientPropValues: BarGradientProps;
}

export interface HistogramData {
    COG: {
        b1: {
            histogram: number[][];
            count: number;
        };
    };
}

export interface TargetData {
    id: string;
    basin: string;
    country: string;
}

export enum SnapPointIds {
    preview = 'preview',
    half = 'half',
    full = 'full'
}

export type SnapPoints = {
    [key in SnapPointIds]: number;
};

export type DrawerWithCardsProps = {
    activeCardIndex: number;
    children: ReactNode;
    'data-testid'?: string;
    onClose: Exclude<DrawerProps['onClose'], undefined>;
    open: boolean;
    paperRef?: ForwardedRef<HTMLDivElement>;
    setSnapPoint: (s: SnapPointIds) => void;
    shareable?: boolean;
    snapPoint: SnapPointIds;
    title?: string;
    resetScrollOnChange?: string;
};

export interface EmissionDrawerCardData {
    targetId: string;
    targetGroupId?: string;
    itemId?: string;
    startDateTime: string;
    selectedCoordinates?: number[];
    distinctPointEmissions: DistinctPointSourceAccordionProps['info'][];
    dispersedSourceEmissions: (DispersedSourceAccordionProps['info'] | TotalOnlyAccordionProps['info'])[];
    dataUnavailable?: boolean;
}

export interface EmissionDrawerCardContentProps extends Omit<EmissionDrawerCardData, 'selectedCoordinates'> {
    expandedId: string | null;
    isActive: boolean;
    handleAccordionChange: (id: string, info?: PickInfoToPrioritize | TotalOnlyInfo) => void;
    /** Passed from emissions-map page, centralizes app updates when the date changes */
    handleDateChange: (day: StacDateBlock, targetId: string) => void;
    ogiNearbyEnabled: boolean;
    page?: PageIds;
}

export interface DrawerCardData {
    titleLabel: string;
    titleValue?: string;
    locationSubtitle?: string;
    Content: string | ReactElement;
}

export interface DrawerCardContentProps extends DrawerCardData {}

export interface BitmapInfo {
    pixel: number[];
    uv: number[];
    size: {
        width: number;
        height: number;
    };
    coordinate: number[];
    layerId: string;
}

export function isBitmapInfo(data: unknown): data is BitmapInfo {
    if (!data) return false;
    if (typeof data !== 'object') return false;
    return 'pixel' in data && 'uv' in data && 'size' in data;
}

export interface PlumeEmissionPickInfo
    extends PickInfoToPrioritize<
        MSatPickInfo<GeoJSON.Point, PlumeProperties>['object'] & {
            collection: STACCollection;
            parentTargetId: string;
            itemId: string;
            /** item ID of the target/raster stac item that the plume belongs to  */
            relatedRasterItemId?: string;
            /** Algorithm version */
            processorVersion: string;
        }
    > {}
export function isPlumeEmissionInfo(info: MSatPickInfo): info is PlumeEmissionPickInfo {
    return (
        isGeoJsonFeature(info.object) &&
        isPlumeProperties(info.object.properties) &&
        info.layer.id.includes(MethaneLayerIds.plumeEmissionRate) &&
        'collection' in info.object
    );
}

export interface AreaEmissionPickInfo
    extends PickInfoToPrioritize<MSatPickInfo<GeoJSON.Geometry, RasterPointProperties>['object']> {
    layer: MSatPickInfo['layer'] & { itemId: string; parentTargetId: string };
}
export function isAreaEmissionInfo(info: MSatPickInfo): info is AreaEmissionPickInfo {
    return (
        isGeoJsonFeature(info.object) &&
        isRasterPointProperties(info.object.properties) &&
        info.layer.id.includes(MethaneLayerIds.areaEmissionRaster) &&
        'parentTargetId' in info.layer &&
        'itemId' in info.layer
    );
}

/**
 * For pipeline drawers, object must have correct `properties`
 * and `layer.id` must be OGILayerIds.pipelines
 */
export function isPipelineInfo(info: MSatPickInfo): info is MSatPickInfo<GeoJSON.Geometry, PipelineFeatureProperties> {
    return isOGITileFeature(info.object) && info.object.properties.layerName === OGILayerIds.pipelines;
}

export type OGIInfo =
    | MSatPickInfo<GeoJSON.Geometry, PipelineFeatureProperties>
    | MSatPickInfo<GeoJSON.Point, InfrastructureGeoJSONProperties>
    | MSatPickInfo<GeoJSON.Geometry, BasinFeatureProperties>
    | MSatPickInfo<GeoJSON.Geometry, ClusteredOGITileFeatureProperties>;

export function isOGIInfo(info: MSatPickInfo): info is OGIInfo {
    return (
        isPipelineInfo(info) ||
        isClusteredPointInfrastructureInfo(info) ||
        isBasinInfo(info) ||
        isPointInfrastructureInfo(info)
    );
}
/**
 * For target drawers, object must have correct `properties`
 * and `layer.id` must be MethaneLayerIds.targets
 */
export type TargetInfo = MSatPickInfo<GeoJSON.Polygon, CaptureFeatureProperties>;

export function isTargetInfo(info: MSatPickInfo): info is TargetInfo {
    return isCaptureProperties(info.object.properties) && info.layer.id.includes(MethaneLayerIds.targets);
}
export type TargetDrawerProps = Omit<DrawerWithCardsProps, 'children'> & {
    cardData: TargetInfo[];
    /** Passed from emissions-map page, centralizes app updates when the date changes */
    handleInfoOnDateChange: (data: PickInfoToPrioritize[]) => void;
    page?: PageIds;
};

/**
 * For scenes with totals only (no COG)
 */
export type TotalOnlyInfo = MSatPickInfo<GeoJSON.Polygon, CaptureFeatureProperties>;

export function isTotalOnlyInfo(info: MSatPickInfo): info is TotalOnlyInfo {
    return isCaptureProperties(info.object.properties) && info.layer.id.includes('total-only');
}

/** Area emissions info or total-only info */
export type RasterLayerInfo = AreaEmissionPickInfo | TotalOnlyInfo;

/** Returns `true` if input is `AreaEmissionPickInfo` or `TotalOnlyInfo` */
export function isRasterInfo(info: MSatPickInfo): info is RasterLayerInfo {
    return isAreaEmissionInfo(info) || isTotalOnlyInfo(info);
}

/**
 * For point infrastructure drawers, object must have  point geometry, correct `properties`
 * and `properties.layerName` included in point OGIPointInfrastructureIds
 */
export function isPointInfrastructureInfo(
    info: MSatPickInfo
): info is MSatPickInfo<GeoJSON.Point, InfrastructureGeoJSONProperties> {
    return (
        isPointFeature(info.object) &&
        isOGITileFeature(info.object) &&
        Object.values(OGIPointInfrastructureIds).includes(
            info.object.properties.layerName as unknown as OGIPointInfrastructureIds
        )
    );
}

/**
 * Feature must pass checks for `isPointInfrastructureInfo` and include
 * `clustered` and `point_count` properties.
 */
export function isClusteredPointInfrastructureInfo(
    info: MSatPickInfo
): info is MSatPickInfo<GeoJSON.Point, ClusteredOGITileFeatureProperties> {
    return isPointInfrastructureInfo(info) && isClusteredPointOGITileFeature(info.object);
}

/**
 * For basin drawers, object must have correct `properties`
 * and `properties.layerName` of `OGILayerIds.basins`
 */
export function isBasinInfo(info: MSatPickInfo): info is MSatPickInfo<GeoJSON.Geometry, BasinFeatureProperties> {
    return isOGITileFeature(info.object) && info.object.properties.layerName === OGILayerIds.basins;
}

/**
 *
 * Allows us to get the first word in multi-word strings from OGI data fields so we can use
 * that word as a key to get the data from locales file.
 */
export function cleanOgiData(ogiData: string, charToSplit: string) {
    return ogiData.split(charToSplit)[0];
}

export type DataRow = {
    label: string | undefined;
    value: number | string | null | boolean | undefined;
    toolTip?: string | undefined;
    mobileLabel?: string;
    icon?: ReactNode;
    href?: string;
};

export interface MapControlAccordionProps extends Omit<MuiAccordionProps, 'children'> {
    /** Child elements to display inside an expanded accordion. */
    children?: ReactNode;
    /** Whether the accordion is initially shown expanded. */
    defaultExpanded?: boolean;
    /** Component(s)for a given layer's control's header. Includes label & optional checkbox to turn layer on/off */
    HeaderContents?: ReactNode;
}
